import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Users, AlertTriangle, Upload } from 'lucide-react';
import { emailService, EmailTemplate, EmailRecipientInput, EmailProvider } from 'api/services/email.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

/** One parsed recipient row: the address plus every column's value keyed by header. */
interface Row { email: string; cells: Record<string, string>; }

/**
 * Parse pasted CSV. First row is a header; a column named `email` (case-insensitive) is the
 * address, every other column is available to map onto a template variable. If there is no
 * `email` header, each line is treated as a bare address.
 */
function parseCsv(text: string): { rows: Row[]; columns: string[]; invalid: number } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { rows: [], columns: [], invalid: 0 };
  const header = lines[0].split(',').map((h) => h.trim());
  const emailIdx = header.findIndex((h) => h.toLowerCase() === 'email');
  const columns = header.filter((_, i) => i !== emailIdx);
  const rows: Row[] = [];
  let invalid = 0;
  const start = emailIdx === -1 ? 0 : 1;
  for (let i = start; i < lines.length; i++) {
    const cells = lines[i].split(',').map((c) => c.trim());
    const email = emailIdx === -1 ? cells[0] : cells[emailIdx];
    if (!email || !email.includes('@')) { invalid++; continue; }
    const cellMap: Record<string, string> = {};
    if (emailIdx !== -1) header.forEach((h, ci) => { if (ci !== emailIdx) cellMap[h] = cells[ci] || ''; });
    rows.push({ email, cells: cellMap });
  }
  return { rows, columns, invalid };
}

/** How one template variable is filled: from a CSV column, or a fixed value for all rows. */
type Mapping = { mode: 'column' | 'static'; column?: string; value?: string };

const EmailCampaignBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testNote, setTestNote] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [standing, setStanding] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [sendRate, setSendRate] = useState(5);
  const [suppressDays, setSuppressDays] = useState<string>('');
  const [csv, setCsv] = useState('');
  const [mapping, setMapping] = useState<Record<string, Mapping>>({});

  useEffect(() => {
    (async () => {
      const [t, p] = await Promise.all([emailService.listTemplates(), emailService.listProviders()]);
      if (t.success) setTemplates((t.data || []).filter((x) => x.active !== false));
      else setError(t.message || 'Failed to load templates');
      if (p.success) {
        const list = p.data || [];
        setProviders(list);
        const def = list.find((x) => x.isDefault) || list[0];
        if (def?.id) setProviderId(def.id);
      }
      setLoading(false);
    })();
  }, []);

  const parsed = useMemo(() => parseCsv(csv), [csv]);
  const selectedTemplate = templates.find((t) => t.id === templateId);
  const templateVars = selectedTemplate?.variables || [];

  // When the template or the detected columns change, (re)initialise the mapping: auto-map
  // a variable to a same-named column when one exists, else leave it as a blank static value.
  useEffect(() => {
    if (templateVars.length === 0) { setMapping({}); return; }
    setMapping((prev) => {
      const next: Record<string, Mapping> = {};
      for (const v of templateVars) {
        if (prev[v]) { next[v] = prev[v]; continue; }
        const col = parsed.columns.find((c) => c.toLowerCase() === v.toLowerCase());
        next[v] = col ? { mode: 'column', column: col } : { mode: 'static', value: '' };
      }
      return next;
    });
  }, [templateId, parsed.columns.join(',')]);

  const setVarMapping = (v: string, m: Mapping) => setMapping((prev) => ({ ...prev, [v]: m }));

  // Resolve each parsed row into the variables map the backend expects.
  const recipients: EmailRecipientInput[] = useMemo(() => {
    return parsed.rows.map((r) => {
      let variables: Record<string, string>;
      if (templateVars.length > 0) {
        variables = {};
        for (const v of templateVars) {
          const m = mapping[v] || { mode: 'static', value: '' };
          variables[v] = m.mode === 'column' ? (r.cells[m.column || ''] || '') : (m.value || '');
        }
      } else {
        // No declared template variables: pass the raw columns through, as before.
        variables = r.cells;
      }
      return { email: r.email, variables };
    });
  }, [parsed.rows, templateVars, mapping]);

  // Variables that resolve to empty for every row — the pre-send warning.
  const emptyVars = useMemo(() => {
    if (templateVars.length === 0 || recipients.length === 0) return [];
    return templateVars.filter((v) => recipients.every((r) => !r.variables || !r.variables[v]));
  }, [templateVars, recipients]);

  /**
   * Load a CSV or XLSX and feed it into the SAME csv state the textarea uses.
   *
   * Deliberately one code path: if an upload built recipients directly it would skip
   * the column-mapping step below, and pasted and uploaded sheets would behave
   * differently for the same file. Converting back to CSV text keeps the preview,
   * the mapping and the row count identical either way.
   */
  const onFile = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    const r = await emailService.parseRecipients(file);
    setUploading(false);
    if (!r.success || !r.data) { setError(r.message || 'Could not read that file'); return; }

    const { headers, rows, totalRows, truncated } = r.data;
    if (!headers?.length) { setError('That file has no header row'); return; }

    const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const text = [headers.map(esc).join(',')]
      .concat(rows.map((row) => headers.map((h) => esc(row[h] ?? '')).join(',')))
      .join('\n');
    setCsv(text);
    setFileName(file.name);
    // Never silent. A shortened list is how somebody mails 50,000 of 200,000 people
    // and believes the campaign went to everyone.
    setFileNote(truncated
      ? `Read ${rows.length.toLocaleString()} of ${totalRows.toLocaleString()} rows — the rest were not loaded.`
      : `Read ${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'} from ${file.name}.`);
  };

  /**
   * Send one real email, to yourself, before launching to everybody.
   *
   * Uses the FIRST recipient's variables rather than blanks or placeholders, so what
   * arrives is a real render — the misspelled merge field and the token that resolves
   * to nothing both show up, which is the whole reason to send a test. Falls back to
   * empty values only when no recipients have been added yet.
   *
   * Nothing is created: no campaign, no recipient row, no counter moves.
   */
  const sendTest = async () => {
    setError(null);
    setTestNote(null);
    if (!templateId) return setError('Select a template first');
    if (!testTo.trim()) return setError('Enter an address to send the test to');
    setTesting(true);
    const res = await emailService.testSend({
      to: testTo.trim(),
      templateId,
      variables: recipients[0]?.variables || {},
    });
    setTesting(false);
    if (!res.success) return setError(res.message || 'Test send failed');
    // A refusal comes back as a successful call with sent:false — suppression and DND
    // answer this way, and they are the correct outcome, not a fault.
    const d: any = res.data || {};
    setTestNote(d.sent
      ? `Sent to ${testTo.trim()}${recipients[0] ? ' using the first recipient\'s values' : ''}.`
      : `Not sent — ${d.error || d.errorCode || 'the provider refused it'}.`);
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Campaign name is required');
    if (!templateId) return setError('Select a template');
    if (!providerId) return setError('Select a sender');
    // A standing campaign is allowed to start empty — that is the point of one; people
    // arrive later. Requiring a list here would force a fake first recipient.
    if (recipients.length === 0 && !standing) return setError('Add at least one valid recipient');
    setSaving(true);
    const res = await emailService.createCampaign({
      name: name.trim(),
      description: description.trim() || undefined,
      templateId,
      providerId,
      sendRate,
      suppressRecentDays: suppressDays ? Number(suppressDays) : null,
      standing,
      recipients,
    });
    setSaving(false);
    if (res.success && res.data?.id) navigate(`/email/campaigns/${res.data.id}`);
    else setError(res.message || 'Failed to create campaign');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate('/email/campaigns')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">New Email Campaign</h1>

      {error && <ErrorMessage message={error} />}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Campaign name *">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="September newsletter" />
          </Field>
          <Field label="Template *">
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputCls}>
              <option value="">Select a template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Sender *">
            <select value={providerId} onChange={(e) => setProviderId(e.target.value)} className={inputCls}>
              <option value="">Select a sender…</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (default)' : ''} — {p.fromEmail}</option>
              ))}
            </select>
          </Field>
          <Field label="Description">
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} placeholder="Optional" />
          </Field>
        </div>
        {providers.length === 0 && (
          <div className="flex items-center gap-2 text-sm text-orange-600">
            <AlertTriangle className="w-4 h-4" /> No senders configured. Add one under Email Setup first.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Send rate (emails / second)">
            <input type="number" min={1} value={sendRate} onChange={(e) => setSendRate(Number(e.target.value) || 1)} className={inputCls} />
          </Field>
          <Field label="Suppress if emailed within (days)">
            <input type="number" min={0} value={suppressDays} onChange={(e) => setSuppressDays(e.target.value)} className={inputCls} placeholder="Leave blank to disable" />
          </Field>
        </div>

        <Field label="Recipients (CSV — first row is a header, one column named `email`)">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
              <Upload className="w-4 h-4" />
              {uploading ? 'Reading…' : 'Upload CSV or Excel'}
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { onFile(e.target.files?.[0] || null); e.target.value = ''; }}
              />
            </label>
            {fileName && <span className="text-xs text-gray-500 dark:text-gray-400">{fileName}</span>}
          </div>
          {fileNote && (
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">{fileNote}</p>
          )}
          <textarea value={csv} onChange={(e) => { setCsv(e.target.value); setFileNote(null); setFileName(null); }} rows={8}
            className={`${inputCls} font-mono text-xs`}
            placeholder={'email,first_name\nasha@example.com,Asha\nrahul@example.com,Rahul'} />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Uploading fills this box, so you can check and edit exactly what will be sent.
          </p>
        </Field>

        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
          <input type="checkbox" checked={standing} onChange={(e) => setStanding(e.target.checked)} className="mt-1" />
          <span className="text-sm">
            <span className="font-medium text-gray-800 dark:text-gray-100">Keep this campaign open</span>
            <span className="block text-gray-500 dark:text-gray-400 mt-0.5">
              It never completes — you can keep adding people afterwards, one at a time, from a form
              or from an agent after a call. Leave this off for a one-off send to the list above.
            </span>
          </span>
        </label>

        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
            <Users className="w-4 h-4" /> {recipients.length} valid recipient{recipients.length === 1 ? '' : 's'}
          </span>
          {parsed.invalid > 0 && <span className="text-orange-500">{parsed.invalid} row(s) skipped (no valid email)</span>}
        </div>

        {/* Variable mapping — each template {{variable}} maps to a CSV column or a fixed value. */}
        {templateVars.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Map template variables</div>
            {templateVars.map((v) => {
              const m = mapping[v] || { mode: 'static', value: '' };
              return (
                <div key={v} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                  <code className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded justify-self-start">{`{{${v}}}`}</code>
                  <select value={m.mode === 'column' ? `col:${m.column}` : 'static'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'static') setVarMapping(v, { mode: 'static', value: m.value || '' });
                      else setVarMapping(v, { mode: 'column', column: val.slice(4) });
                    }}
                    className={inputCls}>
                    <option value="static">— fixed value —</option>
                    {parsed.columns.map((c) => <option key={c} value={`col:${c}`}>column: {c}</option>)}
                  </select>
                  {m.mode === 'static' ? (
                    <input value={m.value || ''} onChange={(e) => setVarMapping(v, { mode: 'static', value: e.target.value })}
                      className={inputCls} placeholder={`value for {{${v}}}`} />
                  ) : (
                    <span className="text-xs text-gray-400">from CSV column “{m.column}”</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {emptyVars.length > 0 && recipients.length > 0 && (
          <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{emptyVars.map((c) => `{{${c}}}`).join(', ')} resolve to empty for every recipient — map them to a column or set a fixed value.</span>
          </div>
        )}

        {/* Sending a test before launching is the cheap check that catches a broken
            merge field. Without it the only way to see the real thing was to launch a
            throwaway campaign of one, which leaves a fake campaign in every total. */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Send yourself a test</div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendTest(); }}
              placeholder="you@example.com"
              className={`${inputCls} flex-1 min-w-[16rem]`}
            />
            <button onClick={sendTest} disabled={testing || !templateId}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50">
              {testing ? 'Sending…' : 'Send test'}
            </button>
          </div>
          {testNote && <p className="text-xs text-gray-600 dark:text-gray-300">{testNote}</p>}
          <p className="text-[11px] text-gray-400">
            A real email, rendered with the first recipient's values. No campaign is created and
            nothing is counted. Suppression and Do Not Disturb still apply.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={submit} disabled={saving}
            className="tl-btn tl-btn-primary disabled:opacity-50">
            <Send className="w-4 h-4" /> {saving ? 'Creating…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none';
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
    {children}
  </label>
);

export default EmailCampaignBuilder;
